import React from 'react';
import { Descriptions } from 'antd';
import StatusTag from '../StatusTag';
import { STATUSES } from '../../constants/vacancyColumnLabels';


const getBooleanValue = (value) => (value ? 'Да' : 'Нет');

const formatDate = (date) => {
    return date ? new Date(date).toLocaleDateString() : 'Не указана';
};

const formatSoftwareSkills = (skills) => {
    if (!skills || !Array.isArray(skills)) return 'Не указаны';
    return skills.map(skill => `${skill.program} - ${skill.level}`).join(', ');
};

const formatLanguageSkills = (skills) => {
    if (!skills || !Array.isArray(skills)) return 'Не указаны';
    return skills.map(skill => `${skill.language} - ${skill.level}`).join(', ');
};

const VacancyDetailsTab = ({ vacancy, organisations, users }) => {
    const getEntityName = (id, entities) => {
        if (!entities || !Array.isArray(entities)) return 'Не указано';
        const idStr = String(id);
        const entity = entities.find(e => String(e.organisation_id ?? e.id) === idStr);
        return entity ? (entity.name ?? entity.organisation_name ?? 'Не указано') : 'Не указано';
    };

    const getDepartmentName = (departmentId, organisationId) => {
        const orgIdStr = String(organisationId);
        const deptIdStr = String(departmentId);
        const organisation = organisations?.find(org => String(org.organisation_id ?? org.id) === orgIdStr);
        if (organisation && Array.isArray(organisation.departments)) {
            const department = organisation.departments.find(dept => String(dept.id) === deptIdStr);
            return department ? department.name : 'Не указано';
        }
        return 'Не указано';
    };

    const getUserName = (id) => {
        const user = users.find(u => u.id === id);
        return user ? `${user.first_name} ${user.last_name}` : 'Не указано';
    };

    return (
        <div className="vacancy-general-info">
            <Descriptions bordered column={1}>
                <Descriptions.Item label="ID">{vacancy.id}</Descriptions.Item>
                <Descriptions.Item label="Должность">{vacancy.job_title || 'Не указано'}</Descriptions.Item>
                <Descriptions.Item label="Статус">
                    {vacancy.is_template ? <StatusTag status="template" statuses={STATUSES} /> : <StatusTag status={vacancy.status} statuses={STATUSES} />}
                </Descriptions.Item>
                <Descriptions.Item label="Компания">
                    {vacancy.organisation_name || getEntityName(vacancy.organisation_id, organisations)}
                </Descriptions.Item>
                <Descriptions.Item label="Отдел">
                    {vacancy.department_name || getDepartmentName(vacancy.department_id, vacancy.organisation_id)}
                </Descriptions.Item>
                <Descriptions.Item label="Рекрутер">{getUserName(vacancy.assigner_id)}</Descriptions.Item>
                <Descriptions.Item label="Автор">{getUserName(vacancy.author) || 'Не указан'}</Descriptions.Item>
                <Descriptions.Item label="Должность автора">{vacancy.author_position || 'Не указана'}</Descriptions.Item>
                <Descriptions.Item label="Город">{vacancy.city || 'Не указан'}</Descriptions.Item>
                <Descriptions.Item label="Пол">{vacancy.gender || 'Не указан'}</Descriptions.Item>
                <Descriptions.Item label="Возраст">{vacancy.age || 'Не указан'}</Descriptions.Item>
                <Descriptions.Item label="Семейное положение">
                    {vacancy.marital_status?.length ? vacancy.marital_status.join(', ') : 'Не указано'}
                </Descriptions.Item>
                <Descriptions.Item label="Количество позиций">{vacancy.positions_count || 'Не указано'}</Descriptions.Item>
                <Descriptions.Item label="Причина вакансии">{vacancy.vacancy_reason || 'Не указана'}</Descriptions.Item>
                <Descriptions.Item label="Дата создания">{formatDate(vacancy.created_at)}</Descriptions.Item>
                <Descriptions.Item label="Дата открытия">{formatDate(vacancy.open_date)}</Descriptions.Item>
                <Descriptions.Item label="Планируемая дата закрытия">{formatDate(vacancy.planned_close_date)}</Descriptions.Item>
                <Descriptions.Item label="Дата закрытия">{formatDate(vacancy.close_date)}</Descriptions.Item>
                <Descriptions.Item label="Планируемая дата выхода">{vacancy
                    .planned_start_date
                }</Descriptions.Item>
                <Descriptions.Item label="Зарплата на испытательный срок">{vacancy.trial_salary || 'Не указана'}</Descriptions.Item>
                <Descriptions.Item label="Зарплата после испытательного срока">{vacancy.post_trial_salary || 'Не указана'}</Descriptions.Item>
                <Descriptions.Item label="Ставка">{vacancy.rate || 'Не указана'}</Descriptions.Item>
                <Descriptions.Item label="График работы">{vacancy.work_schedule || 'Не указан'}</Descriptions.Item>
                <Descriptions.Item label="Время работы">{vacancy.work_start_time} — {vacancy.work_end_time}</Descriptions.Item>
                <Descriptions.Item label="Адрес рабочего места">{vacancy.workplace_address || 'Не указан'}</Descriptions.Item>
                <Descriptions.Item label="Тип работы">{vacancy.field_vs_office || 'Не указан'}</Descriptions.Item>
                <Descriptions.Item label="Компенсация телефона">{getBooleanValue(vacancy.compensate_phone)}</Descriptions.Item>
                <Descriptions.Item label="Компенсация автомобиля">{getBooleanValue(vacancy.compensate_car)}</Descriptions.Item>
                <Descriptions.Item label="Количество подчиненных">{vacancy.subordinates_count || 0}</Descriptions.Item>
                <Descriptions.Item label="Требуется переезд">{getBooleanValue(vacancy.requires_relocation)}</Descriptions.Item>
                <Descriptions.Item label="Командировки">{getBooleanValue(vacancy.requires_travel)}</Descriptions.Item>
                <Descriptions.Item label="Детали командировок">{vacancy.travel_details || 'Не указаны'}</Descriptions.Item>
                <Descriptions.Item label="Требуются права">{getBooleanValue(vacancy.requires_license)}</Descriptions.Item>
                <Descriptions.Item label="Категории прав">
                    {vacancy.license_categories?.length ? vacancy.license_categories.join(', ') : 'Не указаны'}
                </Descriptions.Item>
                <Descriptions.Item label="Наличие авто">{getBooleanValue(vacancy.requires_car)}</Descriptions.Item>
                <Descriptions.Item label="Образование">
                    {vacancy.education_levels?.length ? vacancy.education_levels.join(', ') : 'Не указано'}
                </Descriptions.Item>
                <Descriptions.Item label="Уровень владения ПК">{vacancy.computer_skills_level || 'Не указан'}</Descriptions.Item>
                <Descriptions.Item label="Навыки 1С">{vacancy.skills_1c || 'Не указаны'}</Descriptions.Item>
                <Descriptions.Item label="Другие программы">{formatSoftwareSkills(vacancy.software_skills)}</Descriptions.Item>
                <Descriptions.Item label="Знание языков">{formatLanguageSkills(vacancy.language_skills)}</Descriptions.Item>
                <Descriptions.Item label="Опыт работы">{vacancy.experience_description || 'Не указан'}</Descriptions.Item>
                <Descriptions.Item label="Профессиональные навыки">{vacancy.professional_skills || 'Не указаны'}</Descriptions.Item>
                <Descriptions.Item label="Личные качества">{vacancy.personal_traits || 'Не указаны'}</Descriptions.Item>
                <Descriptions.Item label="Ожидаемые результаты">{vacancy.expected_results || 'Не указаны'}</Descriptions.Item>
                <Descriptions.Item label="Учет и отчетность">{vacancy.reporting_details || 'Не указана'}</Descriptions.Item>
                <Descriptions.Item label="Функциональные обязанности">
                    {vacancy.functional_responsibilities || 'Не указаны'}
                </Descriptions.Item>
                <Descriptions.Item label="Задачи">{vacancy.tasks || 'Не указаны'}</Descriptions.Item>
                <Descriptions.Item label="Прочие условия">{vacancy.other_conditions || 'Не указаны'}</Descriptions.Item>
                <Descriptions.Item label="Предпочтительные компании">{vacancy.preferred_companies || 'Не указаны'}</Descriptions.Item>
                <Descriptions.Item label="Нежелательные компании">{vacancy.unwanted_companies || 'Не указаны'}</Descriptions.Item>
                <Descriptions.Item label="Скрытая вакансия">{getBooleanValue(vacancy.is_hidden)}</Descriptions.Item>
                <Descriptions.Item label="Шаблон">{getBooleanValue(vacancy.is_template)}</Descriptions.Item>
                <Descriptions.Item label="Есть воронка">{getBooleanValue(vacancy.has_funnel)}</Descriptions.Item>
                <Descriptions.Item label="Причина закрытия">{vacancy.reason_close_request || 'Не указана'}</Descriptions.Item>
            </Descriptions>
            <style>{`
            .vacancy-general-info .ant-descriptions-view table { table-layout: fixed; width: 100%; }
            .vacancy-general-info .ant-descriptions-item-label { width: 33.333%; }
            .vacancy-general-info .ant-descriptions-item-content { width: 66.667%; }
        `}</style>
        </div>
    );
};

export default VacancyDetailsTab;
